import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronRight, Package, CreditCard, Plus, Store, Truck } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { userService } from '@/services/user.service';
import { orderService } from '@/services/order.service';
import { Address, Prescription } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PrescriptionUploader } from '@/components/common/PrescriptionUploader';
import { formatCurrency } from '@/utils';
import toast from '@/hooks/useToast';

declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => { open(): void };
  }
}

type CheckoutStep = 'address' | 'review' | 'payment';

export const CheckoutPage = () => {
  const { cart, setCart } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [step, setStep] = useState<CheckoutStep>('address');
  const [orderType, setOrderType] = useState<'DELIVERY' | 'PICKUP'>('DELIVERY');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({ line1: '', line2: '', city: 'Nadiad', state: 'Gujarat', pincode: '', label: 'Home' });
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [processing, setProcessing] = useState(false);

  const needsPrescription = cart?.items.some(i => i.product.requiresPrescription);
  const subtotal = cart?.subtotal || 0;
  const deliveryCharge = orderType === 'PICKUP' ? 0 : (subtotal < 500 ? 50 : 0);
  const total = subtotal + deliveryCharge;

  useEffect(() => {
    if (!cart || cart.items.length === 0) { navigate('/cart'); return; }
    userService.getAddresses().then(res => {
      const addrs = res.data.data;
      setAddresses(addrs);
      const def = addrs.find(a => a.isDefault);
      if (def) setSelectedAddress(def.id);
    });
  }, []);

  const addAddress = async () => {
    try {
      const res = await userService.addAddress({ ...newAddress, isDefault: addresses.length === 0 });
      setAddresses(prev => [...prev, res.data.data]);
      setSelectedAddress(res.data.data.id);
      setShowAddressForm(false);
      toast.success('Address saved!');
    } catch { toast.error('Could not save address'); }
  };

  const initiatePayment = async () => {
    if (!selectedAddress) { toast.error('Please select a delivery address'); return; }
    if (needsPrescription && !prescription) { toast.error('Prescription required', 'Please upload your prescription.'); return; }

    setProcessing(true);
    try {
      const res = await orderService.createOrder({
        deliveryAddressId: orderType === 'DELIVERY' ? selectedAddress : undefined,
        prescriptionId: prescription?.id,
        paymentMethod: 'razorpay',
        orderType,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { razorpayOrderId, amount, keyId, order, demoMode } = res.data.data as any;

      if (demoMode || !keyId || keyId.includes('placeholder')) {
        // Demo mode: simulate payment without Razorpay
        await orderService.verifyPayment({
          razorpay_order_id: razorpayOrderId,
          razorpay_payment_id: `demo_pay_${Date.now()}`,
          razorpay_signature: 'demo_signature',
        });
        setCart({ ...cart!, items: [], subtotal: 0 });
        navigate(`/order-confirmation/${order.orderNumber}`);
        return;
      }

      const options = {
        key: keyId,
        amount,
        currency: 'INR',
        name: 'Milan Medical',
        description: `Order #${order.orderNumber}`,
        order_id: razorpayOrderId,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#0D7377' },
        handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await orderService.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setCart({ ...cart!, items: [], subtotal: 0 });
            navigate(`/order-confirmation/${order.orderNumber}`);
          } catch { toast.error('Payment verification failed. Contact support.'); }
        },
        modal: { ondismiss: () => setProcessing(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not initiate payment';
      toast.error(msg);
      setProcessing(false);
    }
  };

  const steps: { key: CheckoutStep; label: string; icon: React.ElementType }[] = [
    { key: 'address', label: 'Address', icon: MapPin },
    { key: 'review', label: 'Review', icon: Package },
    { key: 'payment', label: 'Payment', icon: CreditCard },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="font-heading font-bold text-2xl text-gray-800 mb-8">Checkout</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${step === s.key ? 'bg-primary-500 text-white' : i < steps.findIndex(x => x.key === step) ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>
              <s.icon className="h-4 w-4" />
              {s.label}
            </div>
            {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-gray-300" />}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* Step 1: Address */}
          {step === 'address' && (
            <div className="bg-white rounded-card shadow-card p-6">
              <h2 className="font-heading font-semibold text-lg text-gray-800 mb-4">How would you like to receive your order?</h2>

              {/* Pickup / Delivery toggle */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  onClick={() => setOrderType('DELIVERY')}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${orderType === 'DELIVERY' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-primary-200'}`}
                >
                  <Truck className={`h-5 w-5 ${orderType === 'DELIVERY' ? 'text-primary-600' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <p className={`font-semibold text-sm ${orderType === 'DELIVERY' ? 'text-primary-700' : 'text-gray-700'}`}>Home Delivery</p>
                    <p className="text-xs text-gray-400">{subtotal >= 500 ? 'Free delivery' : '₹50 delivery charge'}</p>
                  </div>
                </button>
                <button
                  onClick={() => setOrderType('PICKUP')}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${orderType === 'PICKUP' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-primary-200'}`}
                >
                  <Store className={`h-5 w-5 ${orderType === 'PICKUP' ? 'text-primary-600' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <p className={`font-semibold text-sm ${orderType === 'PICKUP' ? 'text-primary-700' : 'text-gray-700'}`}>Store Pickup</p>
                    <p className="text-xs text-gray-400">Free · Milan Medical, Nadiad</p>
                  </div>
                </button>
              </div>

              {orderType === 'PICKUP' ? (
                <div className="bg-primary-50 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-primary-700">📍 Milan Medical, Station Road, Nadiad — 387001</p>
                  <p className="text-xs text-primary-600 mt-1">Mon–Sat: 8am–9pm · Sun: 9am–6pm</p>
                  <p className="text-xs text-gray-500 mt-2">You'll receive a token number after placing the order. Come in and show it at the counter.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-medium text-gray-700 mb-3">Select Delivery Address</h3>
                  <div className="space-y-3 mb-4">
                    {addresses.map(addr => (
                      <label key={addr.id} className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedAddress === addr.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-primary-200'}`}>
                        <input type="radio" name="address" value={addr.id} checked={selectedAddress === addr.id} onChange={() => setSelectedAddress(addr.id)} className="mt-1 accent-primary-500" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 text-sm">{addr.label}</span>
                            {addr.isDefault && <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">Default</span>}
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}</p>
                          <p className="text-sm text-gray-500">{addr.city}, {addr.state} — {addr.pincode}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {showAddressForm ? (
                    <div className="border-2 border-dashed border-primary-200 rounded-xl p-5 space-y-3">
                      <h3 className="font-semibold text-gray-800">New Address</h3>
                      <Input placeholder="Address Line 1 *" value={newAddress.line1} onChange={e => setNewAddress(p => ({ ...p, line1: e.target.value }))} />
                      <Input placeholder="Address Line 2" value={newAddress.line2} onChange={e => setNewAddress(p => ({ ...p, line2: e.target.value }))} />
                      <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="City *" value={newAddress.city} onChange={e => setNewAddress(p => ({ ...p, city: e.target.value }))} />
                        <Input placeholder="Pincode *" value={newAddress.pincode} maxLength={6} onChange={e => setNewAddress(p => ({ ...p, pincode: e.target.value }))} />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={addAddress} size="sm">Save Address</Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowAddressForm(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setShowAddressForm(true)} className="flex items-center gap-2 text-primary-600 text-sm font-semibold hover:text-primary-700">
                      <Plus className="h-4 w-4" /> Add New Address
                    </button>
                  )}
                </>
              )}

              <Button className="mt-6 w-full" onClick={() => setStep('review')} disabled={orderType === 'DELIVERY' && !selectedAddress}>
                Continue to Review
              </Button>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 'review' && (
            <div className="bg-white rounded-card shadow-card p-6">
              <h2 className="font-heading font-semibold text-lg text-gray-800 mb-5">Review Your Order</h2>
              <div className="space-y-4 mb-6">
                {cart?.items.map(item => (
                  <div key={item.id} className="flex gap-4">
                    <img src={item.product.images[0] || '/placeholder-product.jpg'} alt={item.product.name} className="w-16 h-16 object-cover rounded-xl" />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 text-sm">{item.product.name}</p>
                      <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                      {item.product.requiresPrescription && <span className="badge-rx">Rx Required</span>}
                    </div>
                    <p className="font-bold text-primary-600 text-sm">{formatCurrency(Number(item.product.discountedPrice || item.product.price) * item.quantity)}</p>
                  </div>
                ))}
              </div>

              {needsPrescription && (
                <div className="mb-5">
                  <h3 className="font-semibold text-gray-800 mb-3">Upload Prescription</h3>
                  <PrescriptionUploader onUploaded={setPrescription} />
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('address')}>Back</Button>
                <Button className="flex-1" onClick={() => setStep('payment')} disabled={needsPrescription && !prescription}>
                  Proceed to Payment
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 'payment' && (
            <div className="bg-white rounded-card shadow-card p-6">
              <h2 className="font-heading font-semibold text-lg text-gray-800 mb-5">Payment</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm font-semibold text-amber-800 mb-1">🎭 Demo Mode</p>
                <p className="text-sm text-amber-700">Payments are simulated — no real money is charged. Click the button below to complete the demo order.</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('review')}>Back</Button>
                <Button className="flex-1" loading={processing} onClick={initiatePayment}>
                  Simulate Payment of {formatCurrency(total)}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-card shadow-card p-5 h-fit sticky top-24">
          <h3 className="font-heading font-semibold text-gray-800 mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Items ({cart?.items.length})</span><span>{formatCurrency(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Delivery</span><span>{deliveryCharge === 0 ? <span className="text-success font-medium">FREE</span> : formatCurrency(deliveryCharge)}</span></div>
            <div className="border-t pt-2 flex justify-between font-bold text-base">
              <span>Total</span><span className="text-primary-600">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
